/**
 * Settings controls shared by the System settings page and Learning Settings.
 *
 * They live here rather than in either page because both edit the SAME
 * `tenant_config.features` draft through the same PATCH — two copies of a
 * notification-template editor is two chances for one of them to write a
 * subtly different shape.
 */
import { Button, Checkbox, Input, Textarea } from '@mlm/ui';
import {
  NOTIFICATION_TEMPLATES_DEFAULT,
  type MerchantRequirementDef,
  type NOTIFICATION_EVENT_CODES,
} from '@mlm/shared';
// The admin SPA's own `TenantFeatures` duplicate — the same type settings.tsx
// and every draft on it use. Importing the shared one here would type the draft
// as a DIFFERENT shape and reject it.
import type { TenantFeatures } from '../../lib/api';
import { EVENT_LABELS } from './event-labels';

export function Toggle({
  label,
  description,
  value,
  disabled,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox
        checked={value}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-1"
      />
      <div className="space-y-0.5">
        <div className="text-sm font-medium">{label}</div>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
    </label>
  );
}

/**
 * Wraps MemberPicker for the Default Sponsor setting: resolves a stored
 * user_id back to a display row (name + email) so the picker can show who
 * the current default is without making the parent track that extra state.
 */
export function EventTemplateEditor({
  code,
  draft,
  setDraft,
  canWrite,
}: {
  code: (typeof NOTIFICATION_EVENT_CODES)[number];
  draft: TenantFeatures;
  setDraft: (next: TenantFeatures) => void;
  canWrite: boolean;
}) {
  const nt = draft.notification_templates ?? {
    earnings: NOTIFICATION_TEMPLATES_DEFAULT.earnings,
    events: {},
  };
  const ev = nt.events?.[code] ??
    NOTIFICATION_TEMPLATES_DEFAULT.events?.[code] ?? { enabled: true, title: '', body: '' };
  const setEv = (patch: Partial<typeof ev>) =>
    setDraft({
      ...draft,
      notification_templates: {
        ...nt,
        events: { ...(nt.events ?? {}), [code]: { ...ev, ...patch } },
      },
    });
  return (
    <div className="space-y-2 rounded-md border p-3">
      <Toggle
        label={EVENT_LABELS[code]}
        value={ev.enabled}
        disabled={!canWrite}
        onChange={(v) => setEv({ enabled: v })}
      />
      <Input
        value={ev.title}
        placeholder="Title"
        disabled={!canWrite}
        onChange={(e) => setEv({ title: e.target.value })}
      />
      <Textarea
        rows={2}
        value={ev.body}
        placeholder="Body"
        disabled={!canWrite}
        onChange={(e) => setEv({ body: e.target.value })}
      />
    </div>
  );
}

export function MerchantRequirementsEditor({
  value,
  disabled,
  onChange,
  applicant = 'merchant',
}: {
  value: MerchantRequirementDef[];
  disabled: boolean;
  onChange: (reqs: MerchantRequirementDef[]) => void;
  /** Who applies — the copy is the only difference between the registries. */
  applicant?: 'merchant' | 'creator';
}) {
  function update(i: number, patch: Partial<MerchantRequirementDef>) {
    onChange(value.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Registration requirements</label>
      <p className="text-xs text-muted-foreground">
        What applicants must provide. Text requirements are filled at registration; file
        requirements are uploaded after the {applicant} signs in. The <code>key</code> must be
        lowercase letters/numbers/underscores.
      </p>
      <div className="space-y-2">
        {value.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 rounded border p-2">
            <Input
              className="w-32"
              placeholder="key"
              value={r.key}
              disabled={disabled}
              onChange={(e) =>
                update(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })
              }
            />
            <Input
              className="flex-1 min-w-[160px]"
              placeholder="Label"
              value={r.label}
              disabled={disabled}
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <label className="flex items-center gap-1 text-xs">
              <Checkbox
                checked={r.required}
                disabled={disabled}
                onCheckedChange={(v) => update(i, { required: !!v })}
              />
              Required
            </label>
            <label className="flex items-center gap-1 text-xs">
              <Checkbox
                checked={r.acceptsFile}
                disabled={disabled}
                onCheckedChange={(v) => update(i, { acceptsFile: !!v })}
              />
              File upload
            </label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => onChange(value.filter((_, idx) => idx !== i))}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() =>
          onChange([...value, { key: '', label: '', required: false, acceptsFile: false }])
        }
      >
        Add requirement
      </Button>
    </div>
  );
}

/**
 * Controlled editor for `kyc_requirements`. Each row is an image-upload slot the
 * member fills on their KYC tab, rendered in order. Saved with the rest of the
 * tenant settings via the page's single Save button (no own mutation).
 */
